import { useState, type UIEvent } from 'react'
import { ChatHistory } from '../../chat-page/App'
import { ProfilePopup } from './ProfilePopup'

type SidebarView = 'chat' | 'guides' | 'playbooks' | 'skills'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  mobileOpen?: boolean
  chatHistories: ChatHistory[]
  currentChatId: string | null
  onNewChat: () => void
  onSelectChat: (id: string) => void
  onDeleteChat: (id: string) => void
  onRenameChat: (id: string) => void
  view: SidebarView
  onSetView: (v: SidebarView) => void
  userName: string
  userInitials: string
  userRole?: string
  theme: 'dark' | 'light'
  onToggleTheme: () => void
  onLogOut: () => void
  sessionsLoadingMore?: boolean
  onNavListScroll?: (e: UIEvent<HTMLDivElement>) => void
}

export function Sidebar({
  collapsed, onToggleCollapse,
  mobileOpen = false,
  chatHistories, currentChatId,
  onNewChat, onSelectChat, onDeleteChat, onRenameChat,
  view, onSetView,
  userName, userInitials, userRole,
  theme, onToggleTheme, onLogOut,
  sessionsLoadingMore = false,
  onNavListScroll,
}: SidebarProps) {
  const [search, setSearch] = useState('')
  const [profileOpen, setProfileOpen] = useState(false)

  const filtered = chatHistories.filter(c =>
    c.title.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <>
      <aside
        className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Header */}
        <div className="sb-header">
          <a className="logo-row" href="#" aria-label="AIRA home">
            <div className="logo-mark" aria-hidden="true">
              <svg viewBox="0 0 18 18" fill="none"><path d="M9 1.5L10.6 7.4L16.5 9L10.6 10.6L9 16.5L7.4 10.6L1.5 9L7.4 7.4L9 1.5Z" fill="#000" stroke="#000" strokeWidth="0.4" strokeLinejoin="round"/></svg>
            </div>
            <span className="logo-word sb-hideable">AIRA</span>
          </a>
          <button className="collapse-btn" onClick={onToggleCollapse} aria-label="Toggle sidebar" aria-expanded={!collapsed}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="4" width="12" height="1.3" rx="0.65" fill="currentColor"/><rect x="2" y="7.35" width="8" height="1.3" rx="0.65" fill="currentColor"/><rect x="2" y="10.7" width="10" height="1.3" rx="0.65" fill="currentColor"/></svg>
          </button>
        </div>

        {/* New Chat */}
        <div className="sb-top">
          <button className="new-btn" onClick={onNewChat} aria-label="Start new chat">
            <div className="new-ico" aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 1v8M1 5h8" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
            <span className="sb-hideable">New Chat</span>
          </button>
        </div>

        {/* Search */}
        <div className="sidebar-search-wrap">
          <div className="search-inner">
            <svg viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.2"/><path d="M9 9L11.5 11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
            <input className="search-inp" type="text" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search conversations"/>
          </div>
        </div>

        {chatHistories.length > 0 && <div className="section-lbl sb-hideable">Recent</div>}

        {/* Chat history */}
        <div
          className="nav-list"
          role="list"
          aria-label="Chat history"
          onScroll={onNavListScroll}
        >
          {filtered.map((c, i) => (
            <div
              key={c.id}
              className={`nav-item-row${c.id === currentChatId ? ' active' : ''}`}
              role="listitem"
              style={{ animationDelay: `${i * 0.04}s` }}
              title={collapsed ? c.title : undefined}
            >
              <button
                className="nav-item"
                onClick={() => onSelectChat(c.id)}
                aria-label={`Open chat: ${c.title}`}
              >
                {collapsed ? (
                  <div className="nav-letter" aria-hidden="true">
                    {(c.title?.trim()?.charAt(0) || 'N').toUpperCase()}
                  </div>
                ) : (
                  <div className="nav-dot" aria-hidden="true" />
                )}
                <span className="nav-txt sb-hideable">{c.title}</span>
              </button>
              {!collapsed && (
                <div className="nav-item-actions" aria-hidden={false}>
                  <button
                    className="nav-item-action"
                    onClick={e => { e.stopPropagation(); onRenameChat(c.id) }}
                    aria-label={`Rename chat: ${c.title}`}
                    title="Rename"
                    type="button"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M8.9 2.1l3 3M2 12h3l7.6-7.6a1 1 0 000-1.4l-1.6-1.6a1 1 0 00-1.4 0L2 9v3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="nav-item-action danger"
                    onClick={e => { e.stopPropagation(); onDeleteChat(c.id) }}
                    aria-label={`Delete chat: ${c.title}`}
                    title="Delete"
                    type="button"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M3 4.5h8M5.2 4.5v7m3.6-7v7M5 2.8h4l.6.7H4.4l.6-.7zM4.2 4.5l.4 8a1 1 0 001 1h2.8a1 1 0 001-1l.4-8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && chatHistories.length > 0 && (
            <div className="empty-state sb-hideable">No results found</div>
          )}
          {!collapsed && sessionsLoadingMore && (
            <div className="sb-hideable nav-list-load-more" role="status">
              Loading more…
            </div>
          )}
        </div>

        {/* Nav items */}
        <div style={{ padding: '0 6px', marginTop: 4 }}>
          <button className={`nav-static${view === 'playbooks' ? ' view-active' : ''}`} onClick={() => onSetView('playbooks')} aria-label="Playbooks" title={collapsed ? 'Playbooks' : undefined}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true"><rect x="2" y="2" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="8.5" y="2" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="8.5" y="8.5" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="2" y="8.5" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2"/></svg>
            <span className="sb-hideable">Playbooks</span>
          </button>
          <button className={`nav-static${view === 'skills' ? ' view-active' : ''}`} onClick={() => onSetView('skills')} aria-label="Skills" title={collapsed ? 'Skills' : undefined}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
              <rect x="2" y="3" width="11" height="3" rx="0.6" stroke="currentColor" strokeWidth="1.2" />
              <rect x="3.5" y="6.5" width="8" height="2.5" rx="0.6" stroke="currentColor" strokeWidth="1.2" />
              <rect x="5" y="9.5" width="5" height="2" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            <span className="sb-hideable">Skills</span>
          </button>
          <button className={`nav-static${view === 'guides' ? ' view-active' : ''}`} onClick={() => onSetView('guides')} aria-label="Guides" title={collapsed ? 'Guides' : undefined}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true"><circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.2"/><path d="M7.5 10V7.5M7.5 5.3v.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            <span className="sb-hideable">Guides</span>
          </button>
        </div>

        {/* Footer */}
        <div className="sb-footer">
          <div
            className="profile-row"
            onClick={() => setProfileOpen(o => !o)}
            role="button"
            tabIndex={0}
            aria-label="Profile and settings"
            aria-haspopup="true"
            aria-expanded={profileOpen}
            onKeyDown={e => e.key === 'Enter' && setProfileOpen(o => !o)}
            title={collapsed ? userName : undefined}
          >
            <div className="avatar" aria-hidden="true">{userInitials}</div>
            <div className="p-info sb-hideable">
              <div className="p-name">{userName}</div>
              {userRole && <div className="p-role">{userRole}</div>}
            </div>
            <button
              className={`theme-tog sb-hideable${theme === 'light' ? ' light' : ''}`}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              role="switch"
              aria-checked={theme === 'dark'}
              onClick={e => { e.stopPropagation(); onToggleTheme() }}
            >
              <div className="theme-knob" />
            </button>
          </div>
        </div>

        <ProfilePopup
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          userName={userName}
          userInitials={userInitials}
          userRole={userRole}
          theme={theme}
          onToggleTheme={onToggleTheme}
          onLogOut={onLogOut}
        />
      </aside>
    </>
  )
}
